import { Component } from "@theme/component";
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent } from "@theme/events";

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest(".shopify-section, dialog");

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    // Crossfade support: when the slideshow is in `fade` mode, reflect the current
    // slide onto a `data-fade-active` attribute so the scoped CSS can crossfade.
    // SlideshowSelectEvent bubbles and fires for arrows, thumbnail select, and the
    // next()/previous() calls from the touch-swipe handler below, so this single
    // listener covers every change path.
    this.addEventListener(SlideshowSelectEvent.eventName, this.#markActiveSlide, { signal });

    // Touch-swipe support for the faded gallery. The crossfade CSS removes the
    // native horizontal scroll the slideshow normally derives `current` from, so
    // a swipe would otherwise do nothing on mobile. On a horizontal touch swipe we
    // call next()/previous() (which fire SlideshowSelectEvent → #markActiveSlide).
    this.addEventListener("pointerdown", this.#handlePointerDown, { signal });
    this.addEventListener("pointerup", this.#handlePointerUp, { signal });
    this.addEventListener("pointercancel", this.#handlePointerUp, { signal });
    // Capture-phase guard: swallow the click that follows a swipe so it doesn't
    // also open the zoom dialog (the carousel slide's on:click="…/open/…").
    this.addEventListener("click", this.#handleClickCapture, { capture: true, signal });

    // Mark the initial slide so the first image is visible before any interaction.
    requestAnimationFrame(() => this.#markActiveSlide());
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector("media-gallery");

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Reflects the slideshow's current slide onto a `data-fade-active` attribute so
   * the scoped crossfade CSS in product-media-gallery-content.liquid can fade
   * between stacked slides. Element-identity based (compares against the resolved
   * current slide element) so it can't drift from the slideshow's `current` index.
   * No-op unless the slideshow is in `fade` mode, leaving non-faded galleries
   * (and assets/slideshow.js) untouched.
   */
  #markActiveSlide = () => {
    const slideshow = this.refs.slideshow;
    if (!slideshow || !slideshow.hasAttribute("fade")) return;

    const all = slideshow.refs?.slides ?? [];
    const activeSlide = slideshow.slides?.[slideshow.current];
    all.forEach((slide) => slide.toggleAttribute("data-fade-active", slide === activeSlide));
  };

  /** @type {{ x: number, y: number, time: number } | null} */
  #swipeStart = null;

  /** Whether the last touch interaction was a horizontal swipe (suppresses the trailing click). */
  #didSwipe = false;

  /**
   * Records the start of a potential touch swipe (faded gallery only).
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    if (event.pointerType !== "touch") return;
    if (!this.refs.slideshow?.hasAttribute("fade")) return;
    this.#swipeStart = { x: event.clientX, y: event.clientY, time: event.timeStamp };
    this.#didSwipe = false;
  };

  /**
   * Completes a touch swipe: a horizontal swipe past the threshold advances the
   * slideshow (which crossfades via #markActiveSlide). Mostly-vertical gestures are
   * ignored so the page can still scroll.
   * @param {PointerEvent} event
   */
  #handlePointerUp = (event) => {
    if (event.pointerType !== "touch" || !this.#swipeStart) return;
    const dx = event.clientX - this.#swipeStart.x;
    const dy = event.clientY - this.#swipeStart.y;
    this.#swipeStart = null;
    const THRESHOLD = 40; // px
    // Horizontal swipe only; ignore mostly-vertical (page scroll) gestures.
    if (Math.abs(dx) < THRESHOLD || Math.abs(dx) <= Math.abs(dy)) return;
    const slideshow = this.refs.slideshow;
    if (!slideshow) return;
    this.#didSwipe = true; // suppress the click that would open zoom
    if (dx < 0) slideshow.next(); // swipe left → next
    else slideshow.previous(); // swipe right → previous
  };

  /**
   * Capture-phase click guard: swallows the click that follows a swipe so it does
   * not also open the zoom dialog. Runs before the slide's bubble-phase on:click.
   * @param {Event} event
   */
  #handleClickCapture = (event) => {
    if (!this.#didSwipe) return;
    event.preventDefault();
    event.stopPropagation();
    this.#didSwipe = false;
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Opens the zoom dialog at the slideshow's CURRENT slide. Used by carousel/fade
   * slide click handlers — because fade-mode slides are stacked in the same grid
   * cell, the slide-element that receives the click is not necessarily the slide
   * the user is looking at. Reading slideshow.current resolves the visible slide
   * unambiguously.
   *
   * @param {PointerEvent} event
   */
  openZoomCurrent = (event) => {
    const index = typeof this.slideshow?.current === "number" ? this.slideshow.current : 0;
    this.refs.zoomDialogComponent?.open(index, event);
  };

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get("media-gallery")) {
  customElements.define("media-gallery", MediaGallery);
}
